import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useUpdatePreferences, useUserPreferences } from "@/hooks/useSupabaseData";

export type Currency = "USD" | "EUR" | "GBP" | "JPY" | "BRL" | "CNY";

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  BRL: "R$",
  CNY: "¥",
};

// Approximate static rates (relative to USD)
const RATES: Record<Currency, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.5,
  BRL: 4.97,
  CNY: 7.24,
};

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  symbol: string;
  convert: (usd: number) => number;
  format: (usd: number, decimals?: number) => string;
  allCurrencies: Currency[];
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: "USD",
  setCurrency: () => {},
  symbol: "$",
  convert: (v) => v,
  format: (v) => `$${v.toFixed(2)}`,
  allCurrencies: [],
});

export const useCurrency = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrencyState] = useState<Currency>(
    () => (localStorage.getItem("currency") as Currency) || "USD"
  );
  const { data: prefs } = useUserPreferences();
  const updatePrefs = useUpdatePreferences();

  useEffect(() => {
    if (prefs?.currency && Object.keys(RATES).includes(prefs.currency)) {
      setCurrencyState(prefs.currency as Currency);
    }
  }, [prefs?.currency]);

  const setCurrency = (c: Currency) => {
    setCurrencyState(c);
    localStorage.setItem("currency", c);
    updatePrefs.mutate({ currency: c });
  };

  const convert = (usd: number) => usd * RATES[currency];

  const format = (usd: number, decimals = 2) => {
    const val = convert(usd);
    if (currency === "JPY") decimals = 0;
    return `${CURRENCY_SYMBOLS[currency]}${val.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        symbol: CURRENCY_SYMBOLS[currency],
        convert,
        format,
        allCurrencies: Object.keys(RATES) as Currency[],
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};
